<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import GroupsAPI from 'dashboard/api/whatsappCommonGroups';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  inboxId: { type: Number, default: undefined },
});
const { t } = useI18n();
const inboxGetter = useMapGetter('inboxes/getInbox');
const chat = useMapGetter('getSelectedChat');
const enabled = computed(() => {
  const inbox = inboxGetter.value(props.inboxId);
  return (
    inbox?.additional_attributes?.provider === 'whatsapp_qr' &&
    !chat.value.meta?.sender?.identifier?.startsWith('wa-group:')
  );
});
const groups = ref(null);
const loading = ref(false);
const failed = ref(false);
let revision = 0;
watch(
  () => props.conversationId,
  () => {
    revision += 1;
    groups.value = null;
    loading.value = false;
    failed.value = false;
  }
);

async function load() {
  const requestRevision = ++revision;
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await GroupsAPI.get(props.conversationId);
    if (requestRevision === revision) groups.value = data.groups;
  } catch {
    if (requestRevision === revision) failed.value = true;
  } finally {
    if (requestRevision === revision) loading.value = false;
  }
}
</script>

<template>
  <section v-if="enabled" class="m-2 rounded-xl border border-n-weak p-3">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('WHATSAPP_COMMON_GROUPS.TITLE') }}
        <span v-if="groups">({{ groups.length }})</span>
      </h3>
      <Button
        size="sm"
        variant="faded"
        :label="t('WHATSAPP_COMMON_GROUPS.SHOW')"
        :is-loading="loading"
        :disabled="loading"
        @click="load"
      />
    </div>
    <p class="mt-2 text-xs text-n-slate-11">
      {{ t('WHATSAPP_COMMON_GROUPS.HINT') }}
    </p>
    <p v-if="failed" role="alert" class="mt-2 text-sm text-n-ruby-11">
      {{ t('WHATSAPP_COMMON_GROUPS.ERROR') }}
    </p>
    <p v-else-if="groups?.length === 0" class="mt-2 text-sm text-n-slate-11">
      {{ t('WHATSAPP_COMMON_GROUPS.EMPTY') }}
    </p>
    <ul v-else-if="groups" class="mt-3 grid max-h-64 gap-2 overflow-auto">
      <li
        v-for="group in groups"
        :key="group.id"
        class="min-w-0 rounded-lg bg-n-alpha-2 p-2"
      >
        <p dir="auto" class="break-words text-sm text-n-slate-12">
          {{ group.name }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('WHATSAPP_COMMON_GROUPS.MEMBERS', { count: group.members }) }}
        </p>
      </li>
    </ul>
  </section>
</template>
